/**
 * AI Recruit - Resume Comparison Page
 * AI-powered side-by-side resume comparison with detailed analysis
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  UserGroupIcon,
  SparklesIcon,
  StarIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  ScaleIcon
} from '@heroicons/react/24/outline'
import { FireIcon } from '@heroicons/react/24/solid'

import { resumesApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'

interface Resume {
  id: string
  filename: string
  candidate_name?: string
  candidate_email?: string
  status: string
  quality_score?: number
  years_experience?: number
  analysis?: any
}

interface ComparisonResult {
  resume_id: string
  candidate_name: string
  overall_score: number
  category_scores: Record<string, number>
  strengths: string[]
  weaknesses: string[]
  ranking_position: number
}

interface ResumeComparisonResponse {
  comparison_id: string
  total_candidates: number
  comparison_criteria: Record<string, number>
  results: ComparisonResult[]
  winner_categories: Record<string, string>
  skill_overlap_analysis: Record<string, any>
  ai_insights: Record<string, any>
  execution_time: number
}

const ResumeComparePage: React.FC = () => {
  const location = useLocation()
  const queryIds = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const ids = (params.get('ids') || '').split(',').filter(Boolean)
    return ids.slice(0, 4)
  }, [location.search])

  const [selectedResumes, setSelectedResumes] = useState<string[]>(queryIds)
  const [comparisonData, setComparisonData] = useState<ResumeComparisonResponse | null>(null)
  const [isComparing, setIsComparing] = useState(false)

  // Fetch available resumes
  const { data: resumesData, isLoading } = useQuery({
    queryKey: ['resumes-for-comparison'],
    queryFn: () => resumesApi.list({ status: 'analyzed', page_size: 50 })
  })

  const resumes = resumesData?.data?.resumes || []

  useEffect(() => {
    setSelectedResumes(queryIds)
  }, [queryIds])

  // Call backend comparison API
  const runComparison = async () => {
    if (selectedResumes.length < 2) {
      toast.error('Please select at least 2 resumes to compare')
      return
    }

    setIsComparing(true)
    try {
      const response = await resumesApi.compare({ resume_ids: selectedResumes })
      setComparisonData(response.data as ResumeComparisonResponse)
      toast.success('Comparison analysis completed!')
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Comparison failed'
      toast.error(message)
    } finally {
      setIsComparing(false)
    }
  }

  // Add resume to comparison
  const addResume = (resumeId: string) => {
    if (selectedResumes.length >= 4) {
      toast.error('Maximum 4 resumes can be compared at once')
      return
    }
    if (!selectedResumes.includes(resumeId)) {
      setSelectedResumes([...selectedResumes, resumeId])
    }
  }

  // Remove resume from comparison
  const removeResume = (resumeId: string) => {
    setSelectedResumes(selectedResumes.filter(id => id !== resumeId))
    if (selectedResumes.length <= 2) {
      setComparisonData(null)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600'
    if (score >= 0.6) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getRankIcon = (index: number) => {
    if (index === 0) return <TrophyIcon className="w-5 h-5 text-yellow-500" />
    if (index === 1) return <TrophyIcon className="w-5 h-5 text-gray-400" />
    if (index === 2) return <TrophyIcon className="w-5 h-5 text-orange-600" />
    return <TrophyIcon className="w-5 h-5 text-gray-300" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => window.history.back()}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <ScaleIcon className="w-8 h-8 text-purple-600" />
            <span>AI Resume Comparison</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Compare candidates side-by-side with AI-powered analysis
          </p>
        </div>
      </div>

      {/* Resume Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
          <UserGroupIcon className="w-5 h-5 text-blue-600" />
          <span>Select Resumes to Compare</span>
          <span className="text-sm text-gray-500">({selectedResumes.length}/4 selected)</span>
        </h3>

        {/* Selected Resumes */}
        {selectedResumes.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Selected for Comparison:</h4>
            <div className="flex flex-wrap gap-2">
              {selectedResumes.map(resumeId => {
                const resume = resumes.find((r: Resume) => r.id === resumeId)
                return (
                  <div key={resumeId} className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium">
                      {resume?.candidate_name || resume?.filename || 'Unknown'}
                    </span>
                    <button
                      onClick={() => removeResume(resumeId)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Available Resumes */}
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-8">
              <LoadingSpinner size="sm" />
              <p className="mt-2 text-gray-600">Loading candidate profiles...</p>
            </div>
          ) : resumes.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto" />
              <p className="mt-2 text-gray-600">No analyzed candidates available</p>
            </div>
          ) : (
            resumes.map((resume: Resume) => (
              <div
                key={resume.id}
                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedResumes.includes(resume.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => selectedResumes.includes(resume.id) ? removeResume(resume.id) : addResume(resume.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    selectedResumes.includes(resume.id)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedResumes.includes(resume.id) && (
                      <CheckCircleIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {resume.candidate_name || resume.filename}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {resume.years_experience} years • Quality: {Math.round((resume.quality_score || 0) * 100)}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {resume.quality_score && (
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          className={`w-4 h-4 ${
                            star <= Math.round(resume.quality_score! * 5)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Compare Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={runComparison}
            disabled={selectedResumes.length < 2 || isComparing}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isComparing ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="w-5 h-5" />
                <span>Run AI Comparison</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Comparison Results */}
      {comparisonData && (
        <div className="space-y-6">
          {/* Overall Ranking */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <TrophyIcon className="w-5 h-5 text-yellow-500" />
              <span>AI Ranking & Overall Assessment</span>
            </h3>
            
            <div className="space-y-4">
              {comparisonData.results
                .sort((a, b) => a.ranking_position - b.ranking_position)
                .map((ranking, index) => {
                const candidate = resumes.find((c: Resume) => c.id === ranking.resume_id)
                return (
                  <div key={ranking.resume_id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      {getRankIcon(index)}
                      <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-medium text-gray-900">
                          {ranking.candidate_name || candidate?.candidate_name || candidate?.filename}
                        </h4>
                        <span className={`text-2xl font-bold ${getScoreColor(ranking.overall_score)}`}>
                          {Math.round(ranking.overall_score * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Category Winners */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Category Leaders</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(comparisonData.winner_categories).map(([category, winnerId]) => {
                const winner = resumes.find((c: Resume) => c.id === winnerId)
                return (
                  <div key={category} className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <h4 className="font-medium text-green-900 capitalize mb-2">
                      {category.replace('_', ' ')}
                    </h4>
                    <p className="text-sm text-green-700">
                      {winner?.candidate_name || winner?.filename}
                    </p>
                    <TrophyIcon className="w-6 h-6 text-green-600 mx-auto mt-2" />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Detailed Comparison */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Comparison</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {comparisonData.results
                .sort((a, b) => a.ranking_position - b.ranking_position)
                .map((result) => (
                <div key={result.resume_id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {result.candidate_name}
                  </h4>
                  
                  {/* Strengths */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-green-900 mb-2 flex items-center space-x-1">
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Strengths</span>
                    </h5>
                    <ul className="text-sm text-green-700 space-y-1">
                      {result.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start space-x-1">
                          <span className="text-green-500 mt-0.5">•</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Weaknesses */}
                  <div>
                    <h5 className="text-sm font-medium text-amber-900 mb-2 flex items-center space-x-1">
                      <ExclamationTriangleIcon className="w-4 h-4" />
                      <span>Areas for Development</span>
                    </h5>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {result.weaknesses.map((weakness, index) => (
                        <li key={index} className="flex items-start space-x-1">
                          <span className="text-amber-500 mt-0.5">•</span>
                          <span>{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-purple-900 mb-4 flex items-center space-x-2">
              <SparklesIcon className="w-5 h-5" />
              <span>AI Recommendation</span>
            </h3>
            <p className="text-purple-800 leading-relaxed">
              {comparisonData.ai_insights?.recommendation}
            </p>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium text-purple-900">Skill Overlap:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(comparisonData.skill_overlap_analysis?.common_skills || []).map((skill: any, index: number) => (
                    <span key={index} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                      {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResumeComparePage